import { z } from "zod";

import type { LeadFormPayload } from "@/types/application";

/** Same as Merchant `COMPANY_NAME_REGEX`. */
const COMPANY_NAME_REGEX = /^[A-Za-z0-9\s&().,'-]*$/;
/** Same as Merchant `ALPHABET_NAME_REGEX`. */
const ALPHABET_NAME_REGEX = /^[A-Za-z\s'-]+$/;
/** Same as Merchant `WEBSITE_LINK_REGEX`. */
const WEBSITE_LINK_REGEX =
  /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}([\/?#].*)?$/;
/**
 * Same as Merchant `UAE_MOBILE_REGEX` for the local 9-digit form we collect
 * (optional leading 0 is stripped before validate).
 * Pattern: 5 then one of 0/2/4/5/6/8 then 7 digits.
 */
const UAE_MOBILE_REGEX = /^5[024568]\d{7}$/;
/** Same as Merchant `EMAIL_REGEX`. */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const leadFormSchema = z.object({
  company_name: z
    .string()
    .trim()
    .min(1, "Please enter your company name")
    .min(3, "Company name is too short")
    .max(100, "Company name is too long")
    .regex(COMPANY_NAME_REGEX, "Company name has invalid characters"),
  company_type: z.string().min(1, "Please select a company type"),
  company_website: z
    .string()
    .trim()
    .max(100, "Website URL is too long")
    .refine((value) => !value || WEBSITE_LINK_REGEX.test(value), {
      message: "Please enter a valid website",
    }),
  first_name: z
    .string()
    .trim()
    .min(1, "Please enter your first name")
    .min(2, "First name is too short")
    .max(50, "First name is too long")
    .regex(ALPHABET_NAME_REGEX, "Must contain only alphabets"),
  last_name: z
    .string()
    .trim()
    .min(1, "Please enter your last name")
    .max(50, "Last name is too long")
    .regex(ALPHABET_NAME_REGEX, "Must contain only alphabets"),
  phone_number: z
    .string()
    .min(1, "Mobile number is required")
    .regex(/^[0-9]+$/, "Digits only")
    .regex(
      UAE_MOBILE_REGEX,
      "Only valid UAE mobile numbers are allowed (e.g. 501234567)",
    ),
  country_code: z.string().default("+971"),
  email: z
    .string()
    .trim()
    .min(1, "Please enter your email")
    .max(100, "Email is too long")
    .regex(EMAIL_REGEX, "Please enter a valid email address")
    .transform((value) => value.toLowerCase()),
});

export const emptyLeadForm: LeadFormPayload = {
  company_name: "",
  company_type: "",
  company_website: "",
  first_name: "",
  last_name: "",
  phone_number: "",
  country_code: "+971",
  email: "",
};

export function parseLeadForm(form: LeadFormPayload): {
  success: true;
  data: LeadFormPayload;
} | {
  success: false;
  fieldErrors: Record<string, string>;
} {
  const parsed = leadFormSchema.safeParse({ ...form, country_code: "+971" });
  if (!parsed.success) {
    const fieldErrors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      const key = String(issue.path[0]);
      if (!fieldErrors[key]) fieldErrors[key] = issue.message;
    }
    return { success: false, fieldErrors };
  }
  return {
    success: true,
    data: { ...parsed.data, country_code: "+971" },
  };
}
